import { Options as RichTextRendererOptions } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import {
  renderRichText,
  RenderRichTextData,
} from 'gatsby-source-contentful/rich-text';
import React from 'react';

import {
  AssetHyperlinkInlineRenderer,
  BlockquoteRenderer,
  BoldTextRenderer,
  CodeTextRenderer,
  EmbeddedAssetBlockRenderer,
  EmbeddedEntryBlockRenderer,
  EmbeddedEntryInlineRenderer,
  EntryHyperlinkRenderer,
  GroupingRenderer,
  Heading1Renderer,
  Heading2Renderer,
  Heading3Renderer,
  Heading4Renderer,
  Heading5Renderer,
  Heading6Renderer,
  HorizontalRuleRenderer,
  HyperlinkRenderer,
  ItalicTextRenderer,
  ListItemRenderer,
  OrderedListRenderer,
  ParagraphRenderer,
  TableCellRenderer,
  TableHeaderCellRenderer,
  TableRenderer,
  TableRowRenderer,
  TextRenderer,
  UnderlineTextRenderer,
  UnorderedListRenderer,
} from './RichText.renderers';
import { RichTextOptions } from './types';

interface RichTextProps extends RichTextOptions {
  // TODO Replace `never` typing to specify content types that we support embedding within RichText
  value: RenderRichTextData<never>;
}

const getRenderers = (options: RichTextOptions): RichTextRendererOptions => {
  return {
    renderMark: {
      [MARKS.BOLD]: BoldTextRenderer,
      [MARKS.ITALIC]: ItalicTextRenderer,
      [MARKS.UNDERLINE]: UnderlineTextRenderer,
      [MARKS.CODE]: CodeTextRenderer,
    },
    renderNode: {
      [BLOCKS.DOCUMENT]: GroupingRenderer(options),
      [BLOCKS.PARAGRAPH]: ParagraphRenderer,
      [BLOCKS.HR]: HorizontalRuleRenderer,
      [BLOCKS.QUOTE]: BlockquoteRenderer,
      [BLOCKS.HEADING_1]: Heading1Renderer(options),
      [BLOCKS.HEADING_2]: Heading2Renderer(options),
      [BLOCKS.HEADING_3]: Heading3Renderer(options),
      [BLOCKS.HEADING_4]: Heading4Renderer(options),
      [BLOCKS.HEADING_5]: Heading5Renderer(options),
      [BLOCKS.HEADING_6]: Heading6Renderer(options),
      [BLOCKS.OL_LIST]: OrderedListRenderer,
      [BLOCKS.UL_LIST]: UnorderedListRenderer,
      [BLOCKS.LIST_ITEM]: ListItemRenderer,
      [BLOCKS.TABLE]: TableRenderer,
      [BLOCKS.TABLE_ROW]: TableRowRenderer,
      [BLOCKS.TABLE_HEADER_CELL]: TableHeaderCellRenderer,
      [BLOCKS.TABLE_CELL]: TableCellRenderer,
      [BLOCKS.EMBEDDED_ASSET]: EmbeddedAssetBlockRenderer,
      [BLOCKS.EMBEDDED_ENTRY]: EmbeddedEntryBlockRenderer,
      [INLINES.HYPERLINK]: HyperlinkRenderer,
      [INLINES.ENTRY_HYPERLINK]: EntryHyperlinkRenderer,
      [INLINES.ASSET_HYPERLINK]: AssetHyperlinkInlineRenderer,
      [INLINES.EMBEDDED_ENTRY]: EmbeddedEntryInlineRenderer,
    },
    renderText: TextRenderer,
  };
};

const RichText: React.FC<RichTextProps> = ({
  value,
  size,
  alignment,
  mode,
  isContent,
}) => {
  /*
  Note: See this page for information on customizing the renderer for various text elements (bold, paragraph, etc)
  https://www.npmjs.com/package/@contentful/rich-text-react-renderer
  We can also support embedded elements/assets
   */
  // TODO Fix alignment (messes up lists)
  const result = React.useMemo(() => {
    if (!value || !value.raw) {
      return null;
    }
    const renderers = getRenderers({
      alignment,
      size,
      isContent,
      mode: mode,
    });
    return renderRichText(value, renderers);
  }, [alignment, size, mode, value, isContent]);
  return <>{result}</>;
};

export default RichText;
